import { normalizeQcm, trimQcmOptions } from '../normalizers/normalizeQcm';
import { HtmlWithKatex } from '../helpers/renderKatex';
import PrintEnonce, { PrintImg } from '../helpers/PrintEnonce';
import { styleGrilleOptions } from './PrintQcm';
import { CONSIGNES } from '../consignes';
import { CONSIGNES_CAHIER } from '../helpers/saisiePolicy';
import { MAX_GROUPED_OPTIONS, indexFigurePartagee } from '../helpers/groupQuestions';
import { reponseQcmCourte } from '../helpers/reponseLibre';

/**
 * Renderer print d'un BLOC QCM groupé : un seul header "Exercice N — QCM",
 * une seule consigne, puis une liste numérotée 1. / 2. / 3. de sous-questions.
 *
 * Chaque sous-question est trimée à 4 options max (via `trimQcmOptions`).
 * Consigne générique `qcm_multi` : "Entoure la ou les bonnes réponses."
 * couvre les sous-questions simples ET multi sans ambiguïté.
 *
 * `reponseLibre` (axe format de l'aperçu) : chaque sous-question à choix
 * textuels perd ses choix au profit de lignes de réponse (1 ou 2) ; une
 * sous-question à images garde les siens. La consigne de tête suit : saisie
 * si tout est converti, mixte sinon.
 */
const CONSIGNE_MIXTE = 'Réponds à chaque question. Quand des choix sont proposés, entoure la ou les bonnes réponses.';
const CONSIGNE_MIXTE_CAHIER = 'Réponds à chaque question sur ton cahier. Quand des choix sont proposés, écris la ou les lettres des bonnes réponses.';

const consigneDeTete = (enCahier, reponseLibre, nbConvertis, nbTotal) => {
    if (!reponseLibre || nbConvertis === 0) {
        return enCahier ? CONSIGNES_CAHIER.qcm_multi : CONSIGNES.qcm_multi;
    }
    if (nbConvertis === nbTotal) {
        return enCahier ? CONSIGNES_CAHIER.saisie : CONSIGNES.saisie;
    }
    return enCahier ? CONSIGNE_MIXTE_CAHIER : CONSIGNE_MIXTE;
};

const PrintQcmGroup = ({ exNum, items, saisieMode = 'sur_la_feuille', reponseLibre = false, figurePartagee = null }) => {
    const enCahier = saisieMode === 'sur_le_cahier';
    const norms = items.map(({ payload }, idx) => trimQcmOptions(
        normalizeQcm(payload, idx + 1),
        MAX_GROUPED_OPTIONS
    ));
    const nbConvertis = reponseLibre ? norms.filter((n) => !n.hasImages).length : 0;
    return (
        <div className="ep-exo ep-qcm-group">
            <span className="ep-exo-num">{exNum}</span>
            <p className="ep-exo-consigne">
                {consigneDeTete(enCahier, reponseLibre, nbConvertis, items.length)}
            </p>
            {figurePartagee?.element ? (
                <PrintEnonce className="ep-figgroup-figure" texte="" elements={[figurePartagee.element]} />
            ) : null}
            <ol className="ep-qcm-group-list">
                {items.map(({ payload, originalIndex }, idx) => {
                    const { question, options, hasImages } = norms[idx];
                    const libre = reponseLibre && !hasImages;
                    // Grille auto-fit (CSS) ; override Confort si choix rédactionnels
                    // (>30c) + colonnage Compact par seuils sur la PLUS LONGUE option
                    // (classes ep-qcm-cols-* actives uniquement sous .ep-compact).
                    const avgLen = options.reduce(
                        (s, o) => s + (o.kind === 'text' ? (o.content?.length || 0) : 100),
                        0
                    ) / (options.length || 1);
                    const maxLen = options.reduce(
                        (m, o) => Math.max(m, o.kind === 'text' ? (o.content?.length || 0) : 100),
                        0
                    );
                    const colsClass = maxLen < 25 ? 'ep-qcm-cols-4' : maxLen < 60 ? 'ep-qcm-cols-2' : 'ep-qcm-cols-1';
                    const gridClass = hasImages
                        ? 'ep-qcm-visual'
                        : `${avgLen > 30 ? 'ep-qcm-long-choices ' : ''}${colsClass}`;
                    const courte = libre && reponseQcmCourte(payload);
                    const skipIdx = indexFigurePartagee(payload, figurePartagee?.cle);
                    return (
                        <li key={payload?.id ?? `sub-${originalIndex}`} className="ep-qcm-group-item">
                            <PrintEnonce
                                className="ep-qcm-group-question"
                                texte={question}
                                elements={payload?.elements_additionnels?.elements}
                                skipIndices={skipIdx >= 0 ? [skipIdx] : null}
                            />
                            {libre ? (
                                !enCahier && (
                                    courte ? (
                                        <div className="ep-answer-lines ep-answer-lines-short">
                                            <div className="ep-answer-line" />
                                        </div>
                                    ) : (
                                        <div className="ep-answer-lines">
                                            <div className="ep-answer-line" />
                                            <div className="ep-answer-line" />
                                        </div>
                                    )
                                )
                            ) : (
                                <ul className={`ep-qcm-options ${gridClass}`} style={styleGrilleOptions(options)}>
                                    {options.map((opt) => (
                                        <li key={opt.letter} className="ep-qcm-option">
                                            <span className="ep-qcm-letter">{opt.letter}</span>
                                            {opt.kind === 'image'
                                                ? <PrintImg className="ep-qcm-option-img" src={opt.content} alt={opt.alt} />
                                                : <HtmlWithKatex className="ep-qcm-option-text" html={opt.content} />
                                            }
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

export default PrintQcmGroup;
